import React, { useCallback, useEffect, useState } from 'react';
import { 
  Box, Typography, Button, Container, Paper, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, CircularProgress, Alert,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchJson } from './api';

function AdminPage() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wipeDialogOpen, setWipeDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchJson('/reservations');
      if (!Array.isArray(data.reservations)) throw new Error('The server returned an invalid reservation list.');
      setReservations(data.reservations);
    } catch (err) {
      setReservations([]);
      setError(err.message || "Could not connect to the backend.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const handleFreeSeat = async (seat) => {
    setActionLoading(true);
    setError("");
    try {
      await fetchJson('/admin/free-seats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([seat])
      });
      await fetchReservations();
    } catch (err) {
      setError(err.message || "Could not connect to the backend.");
    }
    setActionLoading(false);
  };

  const handleWipeReservations = async () => {
    setActionLoading(true);
    setError("");
    try {
      await fetchJson('/admin/wipe-reservations', {
        method: 'DELETE'
      });
      setWipeDialogOpen(false);
      await fetchReservations();
    } catch (err) {
      setError(err.message || "Could not connect to the backend.");
    }
    setActionLoading(false);
  };

  if (loading && reservations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6, backgroundColor: 'var(--background-color)', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'var(--rosewood)' }}>
          Theater Admin Dashboard
        </Typography>
        <Button 
          variant="contained" 
          color="error" 
          onClick={() => setWipeDialogOpen(true)}
          disabled={actionLoading || reservations.length === 0}
          sx={{ fontWeight: 'bold' }}
        >
          Wipe All Reservations
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
      <Alert role="status" severity="info" sx={{ mb: 3 }}>
        Reservation details and management require a GitHub account invited to the admin role.
        {' '}<a href="/.auth/login/github?post_login_redirect_uri=/admin">Sign in with GitHub</a>.
      </Alert>

      <TableContainer component={Paper} elevation={6} sx={{ borderRadius: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="reservations table">
          <TableHead sx={{ bgcolor: 'var(--crimson)' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Seat</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Email / Phone</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Time</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" color="textSecondary">{error ? 'Reservations are unavailable.' : 'No reservations found.'}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reservations.map((row) => (
                <TableRow
                  key={row.seat}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    {row.seat}
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email && <div>{row.email}</div>}{row.phone}</TableCell>
                  <TableCell>{new Date(row.timestamp).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      startIcon={<DeleteIcon />}
                      onClick={() => handleFreeSeat(row.seat)}
                      disabled={actionLoading}
                    >
                      Free Seat
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog for Wipe All */}
      <Dialog
        open={wipeDialogOpen}
        onClose={() => { if (!actionLoading) setWipeDialogOpen(false); }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ color: 'var(--crimson)', fontWeight: 'bold' }}>
          {"Wipe All Reservations?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. Are you sure you want to delete all theater reservations from the database?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setWipeDialogOpen(false)} disabled={actionLoading} color="inherit" sx={{ fontWeight: 'bold' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleWipeReservations} 
            color="error" 
            variant="contained" 
            disabled={actionLoading}
            sx={{ fontWeight: 'bold' }}
            autoFocus
          >
            {actionLoading ? "Deleting..." : "Yes, Wipe All"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminPage;
